import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent, IconComponent, ImageSelectorComponent } from '@shared/components/ui';
import { DialogRef } from '@ngneat/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GetDatabaseId, GetMemberId, GetTeamId, isMobileView } from '@core/utils';
import { UseUser } from '@core/usecases';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CacheService, ImageConversionService } from '@core/services';
import { SafeUrl } from '@angular/platform-browser';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

@UntilDestroy()
@Component({
  selector: 'app-profile-picture-update-form',
  imports: [CommonModule, ImageSelectorComponent, ButtonComponent, TranslateModule, IconComponent, NgSelectModule, ReactiveFormsModule],
  templateUrl: './profile-picture-update-form.component.html',
  styleUrls: ['./profile-picture-update-form.component.scss'],
})
export class ProfilePictureUpdateFormComponent implements OnInit {
  selectedImage: string | SafeUrl;
  form: FormGroup;
  isLoading = false;
  protected readonly isMobileView = isMobileView();
  private readonly _fb = inject(FormBuilder);
  private readonly _toastService = inject(HotToastService);
  private readonly _translationService = inject(TranslateService);
  private readonly _cacheService = inject(CacheService);
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _useUser = new UseUser();
  private readonly _dialogRef: DialogRef;
  private readonly _bottomSheetService: NgxBottomSheetService;

  constructor() {
    if (isMobileView()) {
      this._bottomSheetService = inject(NgxBottomSheetService);
    } else {
      this._dialogRef = inject(DialogRef);
    }
  }

  ngOnInit() {
    this.form = this._fb.group({
      image: ['assets/images/placeholder.png', [Validators.required]],
    });
    this._clearCache();
    this._useUser
      .getUserPhotoFromLocal(GetMemberId())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (url) => {
          if (url) {
            const imageUrl = this._imageConversionService.convertBase64ToBlobUrl(url);
            this.form.patchValue({ image: imageUrl });
            this.form.markAsUntouched();
            this.form.markAsPristine();
          }
        },
      });
  }

  closeDialog() {
    if (this.isLoading) return;
    if (isMobileView()) {
      this._bottomSheetService.close();
      return;
    }
    this._dialogRef.close();
  }

  save(btnRef?: ButtonComponent) {
    if (this.isLoading || !this.selectedImage) return;
    btnRef?.showLoading();
    this.isLoading = true;
    const imageInBase64 = (this.selectedImage as string).split('base64,')[1];

    this._useUser
      .uploadUserProfileImage(imageInBase64)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this._clearCache();
          setTimeout(() => {
            this._toastService.success(this._translationService.instant('Upload Successful'));
            btnRef?.showSuccess();
            if (isMobileView()) {
              this._bottomSheetService.close(true);
            } else {
              this._dialogRef.close(true);
            }
            this._clearCache();
          }, 7000);
        },
        error: () => {
          this._toastService.error(this._translationService.instant('Something went wrong'));
          btnRef?.showError();
          this.isLoading = false;
        },
      });
  }

  private _clearCache() {
    const normalizedArgs = JSON.stringify([GetMemberId(), GetTeamId(), GetDatabaseId()]);

    const cacheKey = `getUserProfilePhoto-${normalizedArgs}`;

    this._cacheService.delete(cacheKey);
  }
}
